import React, { FC } from "react";
import { Accordion, Icon, Notification, Skeleton } from "@telia/teddy";
import { useQuery } from "@tanstack/react-query";
import { API_URL_ORDER_DOCUMENTS_FOR_GUID, API_URL_REQUEST_DOCUMENTS_LIST } from "../../../api";
import { DOCUMENT_TYPE_LABELS } from "../../../documentTypeLabels";
import { retailerFetch } from "../../../retailerFetch";
import "./ContractSection.less";

interface ContractDocument {
    id: string;
    type: string;
}

interface ContractSectionProps {
    orderId: number | string;
}

interface DocumentsListResponse {
    documents: ContractDocument[];
    orderguid?: string;
}

const fetchDocuments = async (orderId: number | string): Promise<DocumentsListResponse> => {
    const response = await retailerFetch(API_URL_REQUEST_DOCUMENTS_LIST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: String(orderId) }),
    });
    if (!response.ok) {
        throw new Error("Kunne ikke hente kontraktdokumenter.");
    }
    const data = await response.json();
    return { documents: data.documents ?? [], orderguid: data.orderguid };
};

const openDocument = (orderGuid: string, documentId: string) => {
    const newWindow = window.open(
        API_URL_ORDER_DOCUMENTS_FOR_GUID(orderGuid, [documentId]),
        "_blank",
        "noopener,noreferrer",
    );
    // window.open returns null if popup was blocked
    if (newWindow) newWindow.opener = null;
};

const ContractSection: FC<ContractSectionProps> = ({ orderId }) => {
    const { data, isLoading, error } = useQuery({
        queryKey: ["orderDocuments", orderId],
        queryFn: () => fetchDocuments(orderId),
        enabled: Boolean(orderId),
        retry: false,
    });
    const { documents = [], orderguid: orderGuid } = data ?? {};

    return (
        <div className="contract-section">
            {isLoading && <Skeleton height="70px" width="100%" />}
            {!!error && (
                <Notification type="tile" variant="warning">
                    <Notification.Icon />
                    <Notification.Text>
                        {error instanceof Error ? error.message : "Kunne ikke hente kontraktdokumenter."}
                    </Notification.Text>
                </Notification>
            )}
            {!isLoading && !error && documents.length === 0 && (
                <Notification type="tile" variant="warning">
                    <Notification.Icon />
                    <Notification.Text>Ingen kontraktdokumenter tilgjengelig.</Notification.Text>
                </Notification>
            )}
            {!isLoading && !error && documents.length > 0 && (
                <Accordion type="single" noborder collapsible>
                    <Accordion.Item value="contract-documents">
                        <Accordion.Header>
                            <Accordion.Trigger className="contract-section__trigger">{`Dokumenter (${documents.length})`}</Accordion.Trigger>
                        </Accordion.Header>
                        <Accordion.Content>
                            <ul className="contract-section__list">
                                {documents.map((doc) => (
                                    <li key={doc.id}>
                                        <button
                                            type="button"
                                            className="contract-section__link"
                                            disabled={!orderGuid}
                                            onClick={() => orderGuid && openDocument(orderGuid, doc.id)}
                                        >
                                            <Icon name="document-pdf" size="sm" aria-hidden="true" focusable="false" />
                                            {DOCUMENT_TYPE_LABELS[doc.type] ?? doc.type}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </Accordion.Content>
                    </Accordion.Item>
                </Accordion>
            )}
        </div>
    );
};

export default ContractSection;
